import React, { useEffect, useState } from "react";
import { LogOut, Plane, Users, Gauge } from "lucide-react";
import { Button } from "./ui/button";
import AskAIDialog from "./AskAIDialog";
import { toast } from "sonner";
import { getUsage, subscribeUsage, resetUsage, formatUsd } from "../lib/usageMeter";

const COMMUNITY_URL = "https://t.me/+BW7DSK_KE705NTUx";
const USAGE_URL = "https://platform.openai.com/usage";

// Reliable cross-origin opener. Wrapping a <Button> inside an <a target="_blank">
// is flaky (the button can swallow the click and some embeds block the nav),
// so we open the tab imperatively.
//
// IMPORTANT: window.open(url, "_blank", "noopener") ALWAYS returns null per the
// HTML spec — checking the return value to detect popup-blocking is broken and
// would navigate the parent frame away. So we just trust window.open() to do
// the right thing; modern browsers honour user-gesture clicks.
const openExternal = (url) => {
  window.open(url, "_blank", "noopener,noreferrer");
};

// Tiny client-side spend pill. Reads from the usage meter (which sums the
// exact `usage` object returned by every chat completion + estimated
// gpt-image-1 cost). Hidden until the user has actually made a call so the
// header doesn't show a confusing "$0.00" on first paint.
const UsageHint = () => {
  const [u, setU] = useState(getUsage());
  useEffect(() => subscribeUsage(setU), []);
  if (!u.calls) return null;

  const handleReset = () => {
    resetUsage();
    try {
      toast.success("Spend tally reset.");
    } catch {
      /* toast may not be mounted — ignore */
    }
  };

  return (
    <button
      type="button"
      onClick={handleReset}
      data-testid="usage-meter-pill"
      title={`Estimated browser-session spend across ${u.calls} call${u.calls === 1 ? "" : "s"}. Click to reset.`}
      aria-label="Estimated session spend — click to reset"
      className="hidden lg:inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-zinc-900/60 px-2.5 h-9 text-[10px] font-medium text-zinc-400 hover:text-white hover:border-emerald-500/40 hover:bg-emerald-500/5 transition-all"
    >
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400/80" />
      <span className="text-zinc-500">Last</span>
      <span data-testid="usage-meter-last" className="text-emerald-300 tabular-nums">
        {formatUsd(u.lastCallUsd)}
      </span>
      <span className="text-zinc-700">·</span>
      <span className="text-zinc-500">Total</span>
      <span data-testid="usage-meter-total" className="text-zinc-200 tabular-nums">
        {formatUsd(u.totalUsd)}
      </span>
    </button>
  );
};

export const Header = ({ onClearKey, apiKey }) => {
  const handleExit = () => {
    // Disconnect the API key and bounce the user back to the gate screen.
    // No native confirm() — it gets auto-suppressed in some embed contexts
    // and made the button feel "broken". Use a toast for feedback instead.
    onClearKey?.();
    try {
      toast.success("Disconnected. Re-enter your OpenAI key to continue.");
    } catch {
      /* toast may not be mounted yet — ignore */
    }
  };

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-zinc-950/70 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 shrink-0">
          <div className="relative inline-flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 text-[#1c1302] ring-1 ring-amber-300/30">
            <Plane className="h-4 w-4 -rotate-45" strokeWidth={2} />
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="font-display text-lg font-black tracking-tighter text-white">BuddyPass</span>
            <span className="font-display text-lg font-black tracking-tighter text-amber-400">AI</span>
            <span className="text-[10px] font-semibold text-zinc-500">™</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => openExternal(COMMUNITY_URL)}
            data-testid="community-support-link"
            className="rounded-full bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 hover:text-amber-200 border border-amber-500/30 font-semibold text-xs h-9 px-3.5"
          >
            <Users className="h-3.5 w-3.5 mr-1.5" strokeWidth={2} />
            <span className="hidden md:inline">Community Support</span>
            <span className="md:hidden">Support</span>
            <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-red-500/15 border border-red-500/40 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-[0.18em] text-red-300">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-red-500" />
              </span>
              Live
            </span>
          </Button>

          <AskAIDialog apiKey={apiKey} />

          <UsageHint />

          <Button
            variant="ghost"
            size="sm"
            onClick={() => openExternal(USAGE_URL)}
            data-testid="check-usage-link"
            aria-label="Check OpenAI usage"
            title="Open platform.openai.com/usage in a new tab"
            className="rounded-full text-zinc-300 hover:text-white hover:bg-zinc-800 border border-white/10 text-xs h-9 px-3.5"
          >
            <Gauge className="h-3.5 w-3.5 mr-1.5 text-emerald-400" strokeWidth={1.8} />
            <span className="hidden sm:inline">Check usage</span>
            <span className="sm:hidden">Usage</span>
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={handleExit}
            data-testid="clear-api-key-button"
            aria-label="Exit and clear OpenAI key"
            title="Disconnect your OpenAI key and return to the start screen"
            className="rounded-full text-zinc-400 hover:text-white hover:bg-zinc-800 border border-white/10 h-9 px-3.5 text-xs"
          >
            <LogOut className="h-3.5 w-3.5 sm:mr-1.5 text-rose-400" strokeWidth={1.8} />
            <span className="hidden sm:inline">EXIT</span>
          </Button>
        </div>
      </div>
    </header>
  );
};

export default Header;
